import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ChevronDown, Download, Loader2 } from 'lucide-react';
import type { InteractionMessageResponse } from '@vagusx/clients';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import type { HandoffMessage, CallEndedMessage } from '@vagusx/clients';
import { MessageBubble } from './conversation-view/message-bubble';
import { ToolChip } from './conversation-view/tool-chip';
import { AudioBar } from './conversation-view/audio-bar';
import { HandoffRow } from './conversation-view/handoff-row';
import { CallEndedRow } from './conversation-view/call-ended-row';
import { formatDuration } from './conversation-view/format';
import { SilenceGapRow } from './conversation-view/silence-gap-row';
import { RingLeadInRow } from './conversation-view/ring-lead-in-row';
import {
  activeMessageIdAt,
  lineSeekOffsets,
  ringLeadIn,
  silenceGaps,
  type RingLeadIn,
  type SilenceGap,
} from './conversation-view/audio-sync';
import { gutterTimecode, type TimestampMode } from './conversation-view/timecode';
import {
  inSpokenOrder,
  pairToolCalls,
  type ConnectionState,
  type ConversationAudioProps,
  type ConversationViewHeader,
  type ConversationViewProps,
  type PairedEntry,
  type PairedMessages,
} from './conversation-view/types';

// Keep the barrel shape the call sites import — these lived in this file
// before the split. Re-exporting here lets consumers stay on
// `@/components/custom/conversation-view` without touching their imports.
export type {
  ConnectionState,
  ConversationAudioProps,
  ConversationViewHeader,
  ConversationViewProps,
  PairedEntry,
  PairedMessages,
};
export { pairToolCalls } from './conversation-view/types';
export { AudioBar } from './conversation-view/audio-bar';
export { buildTranscriptText, downloadTranscriptAsText } from './conversation-view/format';
export { gutterTimecode } from './conversation-view/timecode';
export type { TimestampMode } from './conversation-view/timecode';

/**
 * ConversationView — pure rendering of a call transcript + tool events.
 *
 * See docs/conversation-view.md for the full design. Used by:
 *   - post-call detail panel
 *   - live-listen page (future)
 *   - test-call panel (task drawer, workflow builder)
 *
 * Data fetching lives in hooks (e.g. `useLiveTranscript`); this
 * component only renders what it's handed. Visual subcomponents
 * (MessageBubble, ToolChip, AudioBar) live alongside in
 * `./conversation-view/`.
 */
export function ConversationView({
  messages,
  showToolCalls = false,
  isLive = false,
  connectionState,
  header,
  audio,
  showAudioBar = true,
  showTimestamps = 'hover',
  emptyState,
  className,
}: ConversationViewProps): React.ReactElement {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);

  // ONE ORDER for the list and the audio sync: the audio helpers below chain
  // each line's start off the previous line's end, so they must see the same
  // sequence of lines the bubbles show.
  const ordered = useMemo(() => inSpokenOrder(messages), [messages]);
  const { entries, responsesByCallId } = useMemo(() => pairToolCalls(ordered), [ordered]);

  // Which line the playhead is inside. Computed once per tick for the whole
  // transcript rather than per bubble — it's an interval question over the
  // full ordered list, and one pass here is also one place to be wrong.
  const activeMessageId = useMemo(
    () => activeMessageIdAt(ordered, audio?.startedAt, audio?.currentMs),
    [ordered, audio?.startedAt, audio?.currentMs]
  );

  // Where clicking a line starts playback. Computed here for the same reason as
  // the highlight: the target is the PREVIOUS line's end, which a bubble cannot
  // see for itself. Not the number the gutter shows — see `lineSeekOffsets`.
  const lineSeeks = useMemo(
    () => lineSeekOffsets(ordered, audio?.startedAt),
    [ordered, audio?.startedAt]
  );

  // Conspicuous waits between lines, keyed by the line each one FOLLOWS — which
  // is the row the marker is drawn under. Without these the transcript reads as
  // if the call had no silences in it, which is the one thing a reviewer opens
  // it to find.
  const gaps = useMemo(() => silenceGaps(ordered), [ordered]);

  // The recording's silent head — egress running while the line still rang.
  // `silenceGaps` deliberately draws nothing before the first line because
  // that interval is not a wait in the conversation; this names it instead,
  // measured between its own two instants. Null is the common case.
  const leadIn = useMemo(
    () => ringLeadIn(audio?.startedAt, audio?.answeredAt, { durationMs: audio?.durationMs }),
    [audio?.startedAt, audio?.answeredAt, audio?.durationMs]
  );

  // Follow the highlighted line while the audio plays, so a reviewer can
  // listen without scrolling. Only WHILE PLAYING: yanking the viewport back
  // on every seek would fight a user who paused to read elsewhere, and
  // `block: 'nearest'` keeps an already-visible line still instead of
  // re-centring the transcript on every line change.
  useEffect(() => {
    if (!activeMessageId || !audio?.isPlaying) return;
    const scroller = scrollerRef.current;
    const el = scroller?.querySelector('[data-active-message="true"]');
    if (!scroller || !el) return;
    // `scrollTop` arithmetic rather than `scrollIntoView`, which adjusts EVERY
    // scrollable ancestor: the detail sheet's body is itself `overflow-y-auto`,
    // so scrolling the line into view there dragged the whole sheet and pushed
    // the player and evaluation card off-screen mid-playback. This moves the
    // transcript's own scroller and nothing else, by the minimum needed —
    // a line already in view does not move at all.
    const scrollerBox = scroller.getBoundingClientRect();
    const lineBox = el.getBoundingClientRect();
    if (lineBox.top < scrollerBox.top) {
      scroller.scrollTop += lineBox.top - scrollerBox.top;
    } else if (lineBox.bottom > scrollerBox.bottom) {
      scroller.scrollTop += lineBox.bottom - scrollerBox.bottom;
    }
  }, [activeMessageId, audio?.isPlaying]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !pinnedToBottom) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, pinnedToBottom]);

  const handleScroll = (): void => {
    const el = scrollerRef.current;
    if (!el) return;
    // 8px slop so sticky audio bar / last-pixel rounding doesn't toggle state.
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    setPinnedToBottom(atBottom);
  };

  const jumpToLatest = (): void => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    setPinnedToBottom(true);
  };

  const banner = renderConnectionBanner(connectionState, isLive);
  const isEmpty = entries.length === 0;

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
      {header !== false && <Header header={header} />}
      {banner}

      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        // `min-h-0` is load-bearing in a flex column — without it
        // `flex-1`'s default `min-height: auto` lets the scroller grow
        // to fit content and the inner overflow never engages. The
        // user reported the call-drawer transcript not scrolling
        // manually or auto-following live messages — same gotcha as
        // TaskListPanel's pagination footer.
        className="relative min-h-0 flex-1 overflow-y-auto px-4 py-3"
      >
        {isEmpty ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {emptyState ?? 'No messages yet.'}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {/* BEFORE every line, because that is what it describes: the file
                was already running when the first one landed. Gutter mode only,
                matching `GapRow` — the ring is part of the time axis, and a
                live transcript has no recording to be ahead of yet. */}
            {showTimestamps === 'always' && leadIn && (
              <LeadInRow leadIn={leadIn} anchorIso={audio?.startedAt} onSeek={audio?.onSeek} />
            )}
            {entries.map((entry) => {
              const gap = showTimestamps === 'always' ? gaps.get(entry.key) : undefined;
              return (
                <Fragment key={entry.key}>
                  <EntryRow
                    entry={entry}
                    responsesByCallId={responsesByCallId}
                    showToolCalls={showToolCalls}
                    audio={audio}
                    activeMessageId={activeMessageId}
                    lineSeeks={lineSeeks}
                    showTimestamps={showTimestamps}
                  />
                  {/* AFTER the line the wait follows, so the marker precedes
                      everything that happened during the silence — including a
                      tool call that fired inside it. Drawn just above the line
                      that ENDS the wait instead, it sorted below such a chip
                      and ran the column backwards.

                      Gutter mode only: the marker is part of the time axis, and
                      a live transcript growing "up to 9s of silence" rows while
                      the reader watches text arrive is noise, not a finding.
                      Every hover-mode surface stays as it was. */}
                  {gap && <GapRow gap={gap} anchorIso={audio?.startedAt} />}
                </Fragment>
              );
            })}
          </div>
        )}

        {!pinnedToBottom && isLive && (
          <button
            type="button"
            onClick={jumpToLatest}
            className="pointer-events-auto sticky bottom-2 left-1/2 z-10 mx-auto flex -translate-x-1/2 items-center gap-1 rounded-full border bg-background px-3 py-1 text-xs shadow-md hover:bg-accent"
          >
            <ChevronDown className="h-3 w-3" />
            Jump to latest
          </button>
        )}
      </div>

      {showAudioBar && audio && (audio.src || audio.callerSrc) && <AudioBar audio={audio} />}
    </div>
  );
}

/**
 * The row every entry kind carries, whatever we call it in the union. Used
 * only for the gutter's timestamp, so a new entry kind gets a timecode without
 * this file having to learn what it is.
 */
function entryMessage(entry: PairedEntry): InteractionMessageResponse {
  if (entry.kind === 'tool_request') return entry.request;
  if (entry.kind === 'handoff' || entry.kind === 'call_ended') return entry.event;
  return entry.message;
}

/**
 * A silence marker laid out to match the rows around it.
 *
 * The gutter cell carries where the wait BEGINS — the end of the line directly
 * above — so it is deliberately the same number that row shows. The line ends
 * there and the silence starts there; the two agreeing is the point, not a
 * duplication.
 *
 * Formatted through `gutterTimecode`, the same call every other row makes, so
 * the marker follows the column into its wall-clock fallback on an unanchored
 * call instead of being the one row that cannot render a cell. The grid wrapper
 * is unconditional for the same reason `EntryRow` draws an empty cell for
 * `call_ended`: the column has to hold its width.
 */
function GapRow({
  gap,
  anchorIso,
}: {
  gap: SilenceGap;
  anchorIso: string | undefined;
}): React.ReactElement {
  const timecode = gutterTimecode(gap.startsAtIso, anchorIso);
  return (
    <div className="grid grid-cols-[3rem_minmax(0,1fr)] items-center gap-2">
      <span
        title={timecode?.title}
        className="text-right font-mono text-[10px] tabular-nums text-muted-foreground"
      >
        {timecode?.label}
      </span>
      <div className="min-w-0">
        <SilenceGapRow gap={gap} />
      </div>
    </div>
  );
}

/**
 * The ring marker in the same gutter grid every other row uses.
 *
 * Its cell is `0:00` because the ring IS position zero of the file — the one
 * row whose timecode is not a lookup but a definition. Passing the anchor as
 * both arguments keeps it going through `gutterTimecode` like everything else,
 * so the column keeps its width and its wall-clock fallback rather than having
 * one row that formats itself.
 */
function LeadInRow({
  leadIn,
  anchorIso,
  onSeek,
}: {
  leadIn: RingLeadIn;
  anchorIso: string | undefined;
  onSeek: ((relativeMs: number) => void) | undefined;
}): React.ReactElement {
  const timecode = anchorIso ? gutterTimecode(anchorIso, anchorIso) : null;
  return (
    <div className="grid grid-cols-[3rem_minmax(0,1fr)] items-center gap-2">
      <span
        title={timecode?.title}
        className="text-right font-mono text-[10px] tabular-nums text-muted-foreground"
      >
        {timecode?.label}
      </span>
      <div className="min-w-0">
        <RingLeadInRow leadIn={leadIn} onSeek={onSeek} />
      </div>
    </div>
  );
}

function EntryRow({
  entry,
  responsesByCallId,
  showToolCalls,
  audio,
  activeMessageId,
  lineSeeks,
  showTimestamps,
}: {
  entry: PairedEntry;
  responsesByCallId: Map<string, InteractionMessageResponse>;
  showToolCalls: boolean;
  audio?: ConversationAudioProps;
  activeMessageId?: string | null;
  lineSeeks?: Map<string, number>;
  showTimestamps: TimestampMode;
}): React.ReactElement | null {
  const gutter = showTimestamps === 'always';
  const content = renderEntry({
    entry,
    responsesByCallId,
    showToolCalls,
    audio,
    activeMessageId,
    lineSeeks,
    // The bubble's own hover timestamp would be a SECOND time on the same row
    // once the gutter is drawn — and a different one, since the gutter states
    // the position in the recording and the bubble states the wall clock.
    showHoverTimestamp: !gutter,
  });
  // Suppressed rows (a tool call with `showToolCalls` off) must not leave an
  // empty gutter cell behind, so the null check happens before the wrapper.
  if (!content || !gutter) return content;

  const message = entryMessage(entry);
  // `call_ended` is placed LAST regardless of its own timestamp — `pairToolCalls`
  // sorts it there because the teardown marker's sequence can beat an in-flight
  // fire-and-forget transcript write (ADR-116). So its timestamp can genuinely
  // precede the line rendered above it, and labelling it would print a column
  // that runs backwards at the foot. Rows ordered by sequence but labelled by
  // timestamp are not monotonic; this is the one row where that shows. The cell
  // is still drawn, so the column keeps its width.
  const timecode =
    entry.kind === 'call_ended'
      ? null
      : gutterTimecode(message.timestamp, audio?.startedAt, {
          // A spoken row's timestamp is the end of its utterance; a tool row's
          // is the instant it fired. Only the former needs saying.
          marksEndOfSpeech: entry.kind === 'user_message' || entry.kind === 'agent_message',
        });

  // Grid, not a flex row: a fixed first track means every timecode lands on the
  // same edge and the column reads as an axis. `w-12` fits `12:34` at this size
  // and holds its width when a cell renders empty.
  return (
    <div className="grid grid-cols-[3rem_minmax(0,1fr)] items-start gap-2">
      <span
        title={timecode?.title}
        className="pt-1.5 text-right font-mono text-[10px] tabular-nums text-muted-foreground"
      >
        {timecode?.label}
      </span>
      <div className="min-w-0">{content}</div>
    </div>
  );
}

function renderEntry({
  entry,
  responsesByCallId,
  showToolCalls,
  audio,
  activeMessageId,
  lineSeeks,
  showHoverTimestamp,
}: {
  entry: PairedEntry;
  responsesByCallId: Map<string, InteractionMessageResponse>;
  showToolCalls: boolean;
  audio?: ConversationAudioProps;
  activeMessageId?: string | null;
  lineSeeks?: Map<string, number>;
  showHoverTimestamp: boolean;
}): React.ReactElement | null {
  if (entry.kind === 'tool_request') {
    if (!showToolCalls) return null;
    const response = entry.request.toolCallId
      ? responsesByCallId.get(entry.request.toolCallId)
      : undefined;
    return <ToolChip request={entry.request} response={response} />;
  }
  if (entry.kind === 'handoff') {
    return <HandoffRow event={entry.event as HandoffMessage} />;
  }
  if (entry.kind === 'call_ended') {
    return <CallEndedRow event={entry.event as CallEndedMessage} />;
  }
  return (
    <MessageBubble
      message={entry.message}
      audio={audio}
      isActive={entry.message.id === activeMessageId}
      seekMs={lineSeeks?.get(entry.message.id) ?? null}
      showHoverTimestamp={showHoverTimestamp}
    />
  );
}

function Header({ header }: { header?: ConversationViewHeader }): React.ReactElement | null {
  if (!header) return null;
  const { liveStatus, showToolCalls, onDownload, additionalActions } = header;
  const hasLeft = !!liveStatus;
  const hasRight = !!showToolCalls || !!onDownload || !!additionalActions;
  if (!hasLeft && !hasRight) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
      <div className="flex min-w-0 items-center gap-2">
        {liveStatus && (
          <span className="flex items-center gap-1.5 text-xs font-medium">
            <span
              className={cn(
                'inline-block h-2 w-2 rounded-full',
                liveStatus.isLive ? 'bg-red-500 animate-pulse' : 'bg-muted-foreground/40'
              )}
            />
            {liveStatus.isLive ? 'Live' : 'Ended'}
            {liveStatus.durationMs != null && (
              <span className="text-muted-foreground">
                · {formatDuration(liveStatus.durationMs)}
              </span>
            )}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {showToolCalls && (
          <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
            <span>Show tool calls</span>
            <Switch
              size="sm"
              checked={showToolCalls.value}
              onCheckedChange={showToolCalls.onChange}
            />
          </label>
        )}
        {onDownload && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={onDownload}
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </Button>
        )}
        {additionalActions}
      </div>
    </div>
  );
}

// Exported for unit tests — the `isLive` gate is the load-bearing bit.
export function renderConnectionBanner(
  state: ConnectionState | undefined,
  isLive: boolean
): React.ReactElement | null {
  // Once the call ends, EventSource fires `error` on the (graceful) server-side
  // close before the hook's cleanup flips state to `disconnected`. Suppress
  // the SSE banner entirely when the call is no longer live — it's a teardown
  // artifact, not a real degradation. (Connecting/connected only happen mid-call,
  // so this gate is correct for both.)
  if (!isLive) return null;
  if (state === 'connecting') {
    return (
      <div className="flex items-center gap-2 border-b bg-amber-50 px-4 py-1.5 text-xs text-amber-900">
        <Loader2 className="h-3 w-3 animate-spin" />
        Connecting…
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="flex items-center gap-2 border-b bg-red-50 px-4 py-1.5 text-xs text-red-900">
        <AlertCircle className="h-3 w-3" />
        Connection error — reconnecting…
      </div>
    );
  }
  return null;
}
